import MarkdownIt from 'markdown-it';
import { full as emoji } from 'markdown-it-emoji';

type Renderer = typeof MarkdownIt.prototype.renderer;
type Token = typeof MarkdownIt.prototype.Token;
type Options = typeof MarkdownIt.prototype.options;

export class NoraMarkdownIt {
  private md: MarkdownIt;

  constructor() {
    this.md = MarkdownIt({
      html: false,
      linkify: true,
      typographer: true,
    })
      .use(emoji)
      .use((md: MarkdownIt) => {
        function renderLink(
          tokens: Token[],
          idx: number,
          opts: Options,
          env: unknown,
          self: Renderer,
        ): string {
          tokens.forEach((token) => {
            if (token.type === 'link_open') {
              token.attrSet('target', '_blank');
              token.attrSet('rel', 'noopener noreferrer');

              const href = token.attrGet('href');

              if (href) {
                token.attrSet('title', href);
                token.attrSet('v-link-guard', '');
              }
            }
          });

          return self.renderToken(tokens, idx, opts);
        }
        // eslint-disable-next-line dot-notation
        md.renderer.rules['link_open'] = renderLink;
      });
  }

  render(value: string): string {
    return this.md.render(value);
  }

  renderInline(value: string): string {
    return this.md.renderInline(value);
  }

  static detectFormat(input: string): string {
    const markdownPatterns = [
      /^#{1,6}\s/m, // Headers
      /(\*\*|__)(.*?)\1/, // Bold
      /(\*|_)(.*?)\1/, // Italic
      /~~(.*?)~~/, // Strikethrough
      /!\[.*?\]\(.*?\)/, // Images
      /\[.*?\]\(.*?\)/, // Links
      /^>\s/m, // Blockquotes
      /^([-+*])\s/m, // Unordered lists
      /^\d+\.\s/m, // Ordered lists
      /`([^`]+)`/, // Inline code
      /^```[\s\S]*?```$/m, // Code blocks
    ];

    const htmlPatterns = [
      /<!DOCTYPE html>/i, // Doctype
      /<html[\s\S]*?>/i, // HTML tag
      /<head[\s\S]*?>/i, // Head tag
      /<body[\s\S]*?>/i, // Body tag
      /<\/?[a-z][\s\S]*?>/i, // Any HTML tag
      /&[a-z]+;/i, // HTML entities
    ];

    const isMarkdown = markdownPatterns.some(pattern => pattern.test(input));
    const isHTML = htmlPatterns.some(pattern => pattern.test(input));

    // Determine the format
    if (isMarkdown && !isHTML) {
      return 'Markdown';
    }
    else if (isHTML && !isMarkdown) {
      return 'HTML';
    }
    else if (isMarkdown && isHTML) {
      return 'Both';
    }
    else {
      return 'Unknown';
    }
  }
}
